/**
 * React Query hooks for API data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type {
  Deal,
  DealWithFund,
  Fund,
  WorkflowExecution,
  DashboardStats,
  CreateDealInput,
  UpdateDealInput,
} from '@/types/database';

// ============================================================================
// DASHBOARD
// ============================================================================

interface DashboardResponse extends DashboardStats {
  dealsByStructure?: Record<string, { count: number; value: number }>;
  recentActivity: Array<{
    id: string;
    action: string;
    deal_name: string | null;
    time: string;
  }>;
}

export function useDashboard() {
  return useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard'),
  });
}

// ============================================================================
// DEALS
// ============================================================================

interface DealsResponse {
  data: Deal[];
  total: number;
  limit: number;
  offset: number;
}

interface DealsFilters {
  stage?: string;
  structure?: string;
  temperature?: string;
  limit?: number;
  offset?: number;
}

export function useDeals(filters?: DealsFilters) {
  const params = new URLSearchParams();
  if (filters?.stage) params.set('stage', filters.stage);
  if (filters?.structure) params.set('structure', filters.structure);
  if (filters?.temperature) params.set('temperature', filters.temperature);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));

  const queryString = params.toString();
  const endpoint = queryString ? `/deals?${queryString}` : '/deals';

  return useQuery<DealsResponse>({
    queryKey: ['deals', filters],
    queryFn: () => api.get(endpoint),
  });
}

interface DealDetailResponse extends DealWithFund {
  fund_name?: string;
  gp_name?: string;
  vintage_year?: number;
  fund_size?: number;
  activities: Array<{
    id: string;
    action: string;
    description: string;
    actor: string;
    created_at: string;
  }>;
}

export function useDeal(id: string | undefined) {
  return useQuery<DealDetailResponse>({
    queryKey: ['deal', id],
    queryFn: () => api.get(`/deals/${id}`),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDealInput) => api.post<Deal, CreateDealInput>('/deals', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDealInput }) =>
      api.put<Deal, UpdateDealInput>(`/deals/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deal', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/deals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ============================================================================
// FUNDS
// ============================================================================

interface FundsResponse {
  data: Fund[];
}

export function useFunds(search?: string) {
  const endpoint = search ? `/funds?search=${encodeURIComponent(search)}` : '/funds';

  return useQuery<FundsResponse>({
    queryKey: ['funds', search],
    queryFn: () => api.get(endpoint),
  });
}

// ============================================================================
// WORKFLOWS
// ============================================================================

interface WorkflowsResponse {
  data: WorkflowExecution[];
}

export function useWorkflowExecutions(status?: string) {
  const endpoint = status ? `/workflows?status=${status}` : '/workflows';

  return useQuery<WorkflowsResponse>({
    queryKey: ['workflows', status],
    queryFn: () => api.get(endpoint),
  });
}

interface RunWorkflowInput {
  workflow_name: string;
  parameters?: Record<string, unknown>;
  deal_id?: string;
}

export function useRunWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RunWorkflowInput) => api.post('/workflows', data),
    onSuccess: () => {
      // Invalidate all relevant queries so data refreshes after workflow
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['funds'] });
    },
  });
}
